import { z } from "zod";
import { projectSchema, type Field, type Project } from "./schema";

/**
 * Host-provided source inputs ("Use Source Input"). A field that opts in asks
 * the parent integration page for its source data through a standard query;
 * the host may return a translation function that maps its own structure into
 * one of the shapes below before the data is used.
 */
export type HostSourceKind = "dataset" | "asset";
export interface HostSourceRequest {
  version: 1;
  kind: HostSourceKind;
  /** Request key; defaults to the field's variable token. */
  key: string;
  fieldId: string;
  label: string;
  type: Field["type"];
}
const cell = z.union([
  z.string().max(32768),
  z.number().finite(),
  z.boolean(),
  z.null(),
]);
/** Dataset sources: a choice list or a table with value/label columns. */
export const hostSourceDatasetSchema = z.union([
  z
    .object({
      choices: z
        .array(
          z
            .object({
              value: z.string().min(1).max(1000),
              label: z.string().max(1000).optional(),
            })
            .strict(),
        )
        .max(10000),
    })
    .strict(),
  z
    .object({
      columns: z
        .array(
          z
            .object({
              key: z.string().min(1).max(100),
              label: z.string().min(1).max(200).optional(),
            })
            .strict(),
        )
        .max(50)
        .optional(),
      rows: z.array(z.record(z.string().min(1).max(100), cell)).max(10000),
      valueColumn: z.string().min(1).max(100),
      labelColumn: z.string().min(1).max(100).optional(),
    })
    .strict(),
]);
/** Asset sources: images the host can serve through its asset loader. */
export const hostSourceAssetsSchema = z
  .object({
    assets: z
      .array(
        z
          .object({
            id: z.string().min(1).max(200),
            name: z.string().min(1).max(500),
            width: z.number().int().positive().max(100000).default(1000),
            height: z.number().int().positive().max(100000).default(1000),
            bytes: z.number().int().nonnegative().default(0),
          })
          .strict(),
      )
      .max(1000),
  })
  .strict();
export type HostSourceDataset = z.infer<typeof hostSourceDatasetSchema>;
export type HostSourceAssets = z.infer<typeof hostSourceAssetsSchema>;
export type HostSourceData = HostSourceDataset | HostSourceAssets;

export function hostSourceKind(field: Pick<Field, "type">): HostSourceKind {
  return ["image", "image-list", "map"].includes(field.type)
    ? "asset"
    : "dataset";
}
/** Fields that request their source from the host, in field order. */
export function hostSourceRequests(project: Project): HostSourceRequest[] {
  return [...project.fields]
    .sort((a, b) => a.order - b.order)
    .filter((field) => field.source.hostInput)
    .map((field) => ({
      version: 1 as const,
      kind: hostSourceKind(field),
      key: field.source.hostInputKey || field.key,
      fieldId: field.id,
      label: field.label,
      type: field.type,
    }));
}
/** Validates translated host data for one request; errors explain the problem. */
export function parseHostSource(
  request: HostSourceRequest,
  data: unknown,
): HostSourceData {
  const schema =
    request.kind === "asset" ? hostSourceAssetsSchema : hostSourceDatasetSchema;
  const parsed = schema.safeParse(data);
  if (!parsed.success)
    throw new Error(
      `${request.label}: the host returned ${
        request.kind === "asset"
          ? "assets that are not { assets: [{ id, name }] }"
          : "data that is not { choices: [{ value, label }] } or { rows, valueColumn }"
      } (${parsed.error.issues[0]?.path.join(".") || "root"}: ${
        parsed.error.issues[0]?.message || "invalid"
      }). Return the expected structure or supply a translate function.`,
    );
  if ("rows" in parsed.data) {
    const table = parsed.data;
    for (const column of [table.valueColumn, table.labelColumn].filter(
      Boolean,
    ) as string[])
      if (table.rows.length && !table.rows.some((row) => column in row))
        throw new Error(
          `${request.label}: column "${column}" is missing from the host rows.`,
        );
  }
  return parsed.data;
}
/**
 * Applies host data to a project copy: dataset sources become pinned, local
 * datasets bound to the field; asset sources become that field's asset list.
 */
export function applyHostSources(
  project: Project,
  results: Record<string, HostSourceData>,
): { project: Project; fieldAssets: Record<string, HostSourceAssets["assets"]> } {
  const copy = structuredClone(project);
  const fieldAssets: Record<string, HostSourceAssets["assets"]> = {};
  for (const [fieldId, data] of Object.entries(results)) {
    const field = copy.fields.find((row) => row.id === fieldId);
    if (!field) continue;
    if ("assets" in data) {
      fieldAssets[fieldId] = data.assets;
      continue;
    }
    const id = `host-source-${fieldId}`.slice(0, 200);
    copy.datasets = copy.datasets.filter((dataset) => dataset.id !== id);
    if ("choices" in data) {
      copy.datasets.push({
        id,
        name: `${field.label} (host source)`.slice(0, 200),
        version: 1,
        kind: "choices",
        choices: data.choices.map((choice) => ({
          value: choice.value,
          label: choice.label ?? choice.value,
        })),
        columns: [],
        rows: [],
      } as Project["datasets"][number]);
      field.source = {
        ...field.source,
        kind: "dataset",
        datasetId: id,
        datasetVersion: 1,
      };
    } else {
      const keys = data.columns?.map((column) => column.key) || [
        ...new Set(data.rows.flatMap((row) => Object.keys(row))),
      ];
      copy.datasets.push({
        id,
        name: `${field.label} (host source)`.slice(0, 200),
        version: 1,
        kind: "table",
        choices: [],
        columns: keys.slice(0, 50).map((key) => ({
          key,
          label:
            data.columns?.find((column) => column.key === key)?.label || key,
          type: "string" as const,
        })),
        rows: data.rows.map((row) =>
          Object.fromEntries(
            keys.map((key) => [
              key,
              row[key] === undefined || row[key] === null
                ? null
                : String(row[key]),
            ]),
          ),
        ),
      } as Project["datasets"][number]);
      field.source = {
        ...field.source,
        kind: "dataset",
        datasetId: id,
        datasetVersion: 1,
        valueColumn: data.valueColumn,
        labelColumn: data.labelColumn || data.valueColumn,
      };
    }
  }
  return { project: copy, fieldAssets };
}

/**
 * Host queries for profile data and part overrides follow the same contract as
 * source inputs: a callback may return the rows directly or `{ data, translate }`,
 * translation runs before use, and the result is validated before display.
 */
export type HostQueryKind = "profiles" | "parts" | "part";
export interface HostQueryRequest {
  version: 1;
  kind: HostQueryKind;
  /** Search text for lists; the selected id for a single part. */
  query: string;
}
export interface HostQueryResult {
  data: unknown;
  translate?: (
    data: unknown,
    request: HostQueryRequest,
  ) => unknown | Promise<unknown>;
}
const hostQueryRowSchema = z
  .object({
    id: z.string().min(1).max(500),
    name: z.string().max(1000),
    sku: z.string().max(500).optional(),
    values: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();
const hostQueryRowsSchema = z.array(hostQueryRowSchema).max(10000);
const hostQueryPartSchema = projectSchema.shape.parts.unwrap().element;

function withSignal<T>(promise: Promise<T>, signal: AbortSignal) {
  return new Promise<T>((resolve, reject) => {
    if (signal.aborted) return reject(signal.reason);
    const abort = () => reject(signal.reason);
    signal.addEventListener("abort", abort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", abort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", abort);
        reject(error);
      },
    );
  });
}
const queryLabels: Record<HostQueryKind, string> = {
  profiles: "Profile data",
  parts: "Part overrides",
  part: "Part override",
};
/** Await, translate and validate one host query response. */
async function resolveHostQuery(
  request: HostQueryRequest,
  response: unknown,
  signal: AbortSignal,
  timeoutMs = 30000,
) {
  const timeout = AbortSignal.timeout(timeoutMs);
  const combined = AbortSignal.any([signal, timeout]);
  const label = queryLabels[request.kind];
  try {
    let value = await withSignal(Promise.resolve(response), combined);
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      "data" in value
    ) {
      const result = value as HostQueryResult;
      value =
        typeof result.translate === "function"
          ? await withSignal(
              Promise.resolve(result.translate(result.data, request)),
              combined,
            )
          : result.data;
    }
    const parsed = (
      request.kind === "part" ? hostQueryPartSchema : hostQueryRowsSchema
    ).safeParse(value);
    if (!parsed.success)
      throw new Error(
        `${label}: the host returned data in an unexpected shape (${parsed.error.issues[0]?.path.join(".") || "root"}: ${parsed.error.issues[0]?.message}).`,
      );
    return parsed.data;
  } catch (error) {
    if (timeout.aborted && !signal.aborted)
      throw new Error(`${label}: the host did not respond in time.`);
    throw error;
  }
}
export type HostQueryRow = z.infer<typeof hostQueryRowSchema>;
/** Profile or part-override rows from a host list/search callback. */
export const resolveHostRows = (
  request: HostQueryRequest & { kind: "profiles" | "parts" },
  response: unknown,
  signal: AbortSignal,
  timeoutMs?: number,
) =>
  resolveHostQuery(request, response, signal, timeoutMs) as Promise<
    HostQueryRow[]
  >;
/** One part override from the host's load callback. */
export const resolveHostPart = (
  id: string,
  response: unknown,
  signal: AbortSignal,
  timeoutMs?: number,
) =>
  resolveHostQuery(
    { version: 1, kind: "part", query: id },
    response,
    signal,
    timeoutMs,
  ) as Promise<z.infer<typeof hostQueryPartSchema>>;
